import React, { useEffect, useId, useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { useWorkspaces } from '../../workspaces/useWorkspaces';
import { useTour } from './useTour';
import { TourStopMessage } from './TourStopMessage';
import { TourGuideActions } from './TourGuideActions';
import { TourSpotlight } from './TourSpotlight';
import { tourMessages as messages } from './tourMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the tour; each part uses its own slice of it */
import styles from './TourGuideBar.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The guide of the LaziiBee tour. The shell mounts it on every page; it renders nothing while the
 * tour does not run. Each stop shows a spotlight: a dim backdrop over the whole page, the page part
 * of the stop bright in a cut-out, and a card next to it with the counter, the message, the
 * progress dots and Back / Next / Exit tour (owner request 2026-10-09: the old bar at the top of
 * the page did not show where to look; copy from design onboarding b1-b7).
 */
const TourGuideBarComponent: React.FC = () => {
  const intl = useIntl();
  const titleId = useId();
  const { stop, index, count, isMember, next, back, exit } = useTour();
  const { active: workspace } = useWorkspaces();
  const dots = useMemo(() => Array.from({ length: count }, (_, dot) => dot), [count]);

  // The current stop on <html>: e2e tests and the page parts can read it.
  useEffect(() => {
    if (stop === null) {
      return undefined;
    }
    const root = document.documentElement;
    root.dataset.tourStop = stop;
    return () => {
      delete root.dataset.tourStop;
    };
  }, [stop]);

  if (stop === null) {
    return null;
  }

  return (
    <TourSpotlight stop={stop} titleId={titleId} onExit={exit}>
      <div className={styles.head}>
        {/* design b1: two lines, the stop number and "of 5". */}
        <div
          className={styles.count}
          data-testid="tour-count"
          role="img"
          aria-label={intl.formatMessage(messages.count, { n: index + 1, count })}
        >
          <b>{intl.formatNumber(index + 1)}</b>
          <small>{intl.formatMessage(messages.countOf, { count })}</small>
        </div>
        <TourStopMessage stop={stop} isMember={isMember} workspaceName={workspace?.name ?? ''} titleId={titleId} />
      </div>
      <div className={styles.foot}>
        <div className={styles.dots} aria-hidden="true">
          {dots.map((dot) => (
            <i key={dot} className={clsx(styles.dot, dot === index && styles.dotOn, dot < index && styles.dotDone)} />
          ))}
        </div>
        <TourGuideActions
          isFirst={index === 0}
          isLast={index === count - 1}
          isMember={isMember}
          onBack={back}
          onNext={next}
          onExit={exit}
        />
      </div>
    </TourSpotlight>
  );
};

export const TourGuideBar = TypedMemo(TourGuideBarComponent);
